import { type ChangeEvent, type FC, useState } from 'react';

import { formatCurrency, formatPhone } from 'utils/format.ts';

import Typography from 'components/Typography';

import visibilityIcon from 'assets/visibility.svg';
import visibilityOffIcon from 'assets/visibility_off.svg';

import {
  CurrencyPrefix,
  HelperText,
  InputContainer,
  InputContent,
  InputIcon,
  InputStyled,
  VisibleInputIcon,
} from './Input.styled.ts';
import type { InputProps } from './Input.types.ts';

const Input: FC<InputProps> = ({
  isValid,
  title,
  icon,
  type,
  variant = 'filled',
  theme = 'light',
  helperText,
  inputType = 'text',
  onChange,
  ...props
}) => {
  const [internalType, setInternalType] = useState(type);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    let filteredValue = newValue;

    switch (inputType) {
      case 'phone':
        filteredValue = newValue.replace(/\D/g, '').substring(0, 10);
        break;

      case 'money':
      case 'number':
        filteredValue = newValue.replace(/\D/g, '');
        break;

      case 'email': {
        const emailChars = /^[a-zA-Z0-9._%+-@]*$/;
        if (!emailChars.test(newValue)) {
          return;
        }
        filteredValue = newValue;
        break;
      }

      case 'text':
      case 'password':
      default:
        filteredValue = newValue;
        break;
    }

    if (
      filteredValue !== newValue ||
      inputType === 'money' ||
      inputType === 'phone'
    ) {
      let displayValue = filteredValue;
      if (inputType === 'money') {
        displayValue = formatCurrency(filteredValue);
      } else if (inputType === 'phone') {
        displayValue = formatPhone(filteredValue);
      }
      e.target.value = displayValue;
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: filteredValue,
        },
      };
      if (onChange) {
        onChange(newEvent as ChangeEvent<HTMLInputElement>);
      }
    } else {
      if (onChange) {
        onChange(e);
      }
    }
  };

  return (
    <InputContainer>
      {title && (
        <Typography
          type="Body"
          size="small"
          color={theme === 'light' ? '#595959' : '#FFFFFF'}
        >
          {title}
        </Typography>
      )}
      <InputContent variant={variant}>
        {icon && <InputIcon src={icon} />}
        {inputType === 'money' && (
          <CurrencyPrefix theme={theme}>$</CurrencyPrefix>
        )}
        <InputStyled
          isValid={isValid}
          variant={variant}
          type={internalType}
          theme={theme}
          onChange={handleChange}
          value={(() => {
            if (inputType === 'money') {
              return props.value && String(props.value).length > 0
                ? formatCurrency(String(props.value))
                : '';
            }
            if (inputType === 'phone') {
              return props.value && String(props.value).length > 0
                ? formatPhone(String(props.value))
                : '';
            }
            return props.value;
          })()}
          {...(() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { value, ...restProps } = props;
            if (inputType === 'phone') {
              return { ...restProps, maxLength: 12 };
            }
            if (inputType === 'money') {
              return restProps;
            }
            return props;
          })()}
        />

        {type === 'password' && (
          <>
            {internalType === 'text' ? (
              <VisibleInputIcon
                src={visibilityIcon}
                onClick={() => setInternalType('password')}
              />
            ) : (
              <VisibleInputIcon
                src={visibilityOffIcon}
                onClick={() => setInternalType('text')}
              />
            )}
          </>
        )}
      </InputContent>
      {helperText && (
        <HelperText>
          <Typography
            type="Label"
            size="medium"
            style="light"
            color={theme === 'light' ? '#595959' : '#FFFFFF'}
          >
            {helperText}
          </Typography>
        </HelperText>
      )}
    </InputContainer>
  );
};

export default Input;
